"use client";
import {
  ButtonComponent,
  CheckboxComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
  SelectComponent,
  TextFieldComponent,
} from "@/components";
import {
  CurrencyConverter,
  IMAGES,
  MOBILE_REGEX,
  NAME_REGEX,
  NOMINEE_SCHEMA,
} from "@/lib/constants";
import useErrorHandling from "@/lib/hooks/errorhandling";
import useLogoutHook from "@/lib/hooks/logoutHook";
import {
  useCreateNomineeMutation,
  useGetInsuranceQuery,
  useGetRelationsQuery,
  useUpdateInsuranceMutation,
} from "@/lib/redux";
import InsurancePlan from "@/screens/protection-plans";
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DatePicker,
  MobileDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Image from "next/image";
import { useRouter } from "@/i18n/routing";
import { setInsuranceDeclaration } from "@/lib/redux/slice/InsuranceSlice";
import { useParams } from "next/navigation";

const INSURANCE_BENEFITS = [
  {
    icon: IMAGES.filledCalendarInsuranceIcon,
    alt: "calendar icon",
    text: "EMI Cover for Job Loss & Critical Illness*",
  },
  {
    icon: IMAGES.filledCoveredLoanInsuranceIcon,
    alt: "Loan covered icon",
    text: "Loan Covered in Case of Death",
  },
  {
    icon: IMAGES.filledOpdInsuranceIcon,
    alt: "OPD icon",
    text: "Tele consultation & OPD Benefits",
  },
];

const NomineeFields = ({
  open,
  handleModal,
  handleOnProceed,
  productApplication,
}) => {
  const t = useTranslations(
    "esign.insuranceBanner.insuranceModal.nomineeFields",
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const router = useRouter();
  const dispatch = useDispatch();
  const { handleLogout } = useLogoutHook();
  const maxDate = dayjs().subtract(1, "year");
  const params = useParams();
  const product = params?.product || "";
  const { insuranceSelected, insuranceDeclaration, selectedPlans } =
    useSelector((s) => s.insurance);
  /***************************************RTK************************************ */
  const { data: relationData, isLoading: isLoadindRealtion } =
    useGetRelationsQuery(null);
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));

  //add nominee details post api call here
  const [
    createNominee,
    {
      data: nomineeDetailsData,
      error: errorNomineeDetailsApi,
      isError: isErrorNomineeDetails,
      isLoading: isLoadindNominee,
      isSuccess: isSuccessNomineeDetails,
    },
  ] = useCreateNomineeMutation();

  const shouldFetch = Boolean(productApplication?.appId);
  const queryArgs = useMemo(() => {
    if (!shouldFetch) return undefined;
    return {
      appId: productApplication?.appId,
    };
  }, [productApplication?.appId, shouldFetch]);

  const {
    data: insuranceData,
    isLoading: loadingInsuranceData,
    isFetching: fetchingInsuranceData,
    error: insuranceLoadingError,
  } = useGetInsuranceQuery(queryArgs, {
    skip: !shouldFetch || !queryArgs,
    refetchOnMountOrArgChange: true,
  });
  const [
    insuranceUpdate,
    {
      data: insuranceUpdateData,
      isLoading: loadingInsuranceUpdateData,
      error: insuranceUpdateDataError,
      isSuccess: successInsuranceUpdateData,
    },
  ] = useUpdateInsuranceMutation();
  const isError =
    errorNomineeDetailsApi ||
    loadingInsuranceData ||
    insuranceLoadingError ||
    insuranceUpdateDataError;
  const isLoading =
    isLoadindRealtion ||
    isLoadindNominee ||
    loadingInsuranceData ||
    loadingInsuranceUpdateData;
  /***************************************EFFECT************************************ */
  useErrorHandling(isError);

  useEffect(() => {
    if (isSuccessNomineeDetails) {
      handleOnProceed();
    }
  }, [isSuccessNomineeDetails]);

  const formik = useFormik({
    initialValues: {
      nomineeDob: productApplication?.nomineeDob,
      nomineeName: productApplication?.nomineeName,
      nomineeRelation: productApplication?.nomineeRelation,
      nomineeMobileNumber: productApplication?.nomineeMobileNumber || "",
    },
    validationSchema: NOMINEE_SCHEMA,
    onSubmit: async (values) => {
      if (
        insuranceData?.data?.isActive &&
        !insuranceData?.data?.insuranceConsent
      ) {
        const insurancePayload = {
          appId: productApplication.appId,
          selectedPlan: selectedPlans,
          selectOfferRequest: {
            offerId: 0,
            newRequestLoanAmount: 0,
            selectedTenure: 0,
          },
          insuranceConsent: true,
          isConsentRequest: true,
        };
        await insuranceUpdate(insurancePayload).unwrap();
      }
      createNominee({
        ...values,
        appId: productApplication?.appId,
        nomineeDob: new Date(formik?.values?.nomineeDob)?.toLocaleDateString(
          "en-GB",
        ),
      });
    },
  });
  /***************************************ACTION************************************ */
  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
    }
  };
  /***************************************RENDER************************************ */
  return (
    <ModalComponent
      maxWidth={isBelowMd ? "sm" : "md"}
      open={open}
      fullScreen={isMobile}
      handleModal={handleModal}
      closeButtonRequired={false}
      contentSx={{
        px: { xs: 1, sm: 2 },
        py: { xs: 0 },
      }}
    >
      <LoaderComponent open={isLoading} />
      <Stack rowGap={1} py={2}>
        <PaperComponent
          noDrop
          paperStyle={{
            borderRadius: "12px",
            my: 0,
            py: 0,
            px: 0,
            overflow: "hidden",
            minWidth: "300px",
          }}
        >
          <Stack>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              backgroundColor={"#E9FFE6"}
              width="100%"
              // minHeight={{ xs: 80, md: 102 }}
              position="relative"
            >
              <Stack
                flexDirection="row"
                alignItems="center"
                gap={{ xs: 1 }}
                px={{ xs: 2 }}
                py={{ xs: 1 }}
              >
                <Image
                  src={IMAGES.surakshaIcon}
                  alt="protection plan icon"
                  width={isMobile ? 30 : 30}
                  height={isMobile ? 30 : 30}
                />
                <Box>
                  <Typography
                    fontWeight={{ md: 700, xs: 500 }}
                    fontSize={{ md: "14px", xs: "14px" }}
                    color={"#004135"}
                  >
                    Protection Plus
                  </Typography>
                  <Typography
                    fontWeight={500}
                    fontSize={{ md: "10px", xs: "8px", sm: "10px" }}
                    color={"#004135"}
                  >
                    One Plan. Triple Protection.
                  </Typography>
                </Box>
              </Stack>
              <Stack alignItems="center" px={{ xs: 1, md: 2 }}>
                <Stack spacing={1}>
                  <Box
                    backgroundColor={"#00F200"}
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    alignSelf={"flex-start"}
                    px={"8px"}
                    py={"4px"}
                    mt={{ xs: 0 }}
                    borderRadius={"30px"}
                    gap={0.5}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5h5" />
                    </svg>
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Typography
                        fontWeight={{ xs: 400 }}
                        fontSize={{ xs: "8px", sm: "10px" }}
                      >
                        Limited Offer
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
            <Stack backgroundColor={"#E9FFE6"} px={2} py={1} mt={-1}>
              <Grid
                container
                justifyContent="space-between"
                alignItems="stretch"
                color="#3F3F3F"
                columnSpacing={{ xs: 1, md: 2 }}
              >
                {INSURANCE_BENEFITS.map((item) => (
                  <Grid item xs={4} key={item.text}>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      borderRadius="8px"
                      border="1px solid #F0F0F0"
                      backgroundColor="#FFFFFF"
                      height={{ xs: 130, md: 120 }}
                      px={{ xs: 1, md: 1 }}
                    >
                      <Image
                        src={item.icon}
                        alt={item.alt}
                        width={isBelowMd ? 40 : 40}
                        height={isBelowMd ? 40 : 40}
                      />

                      <Typography
                        textAlign="center"
                        fontWeight={400}
                        fontSize={{ xs: "10px" }}
                        lineHeight={{ xs: "15px", md: "18px" }}
                        color="#565656"
                        mt={1}
                      >
                        {item.text}
                      </Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              <Stack>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  sx={{ cursor: "pointer" }}
                >
                  <Box mt={1}>
                    <Typography
                      display="inline"
                      fontWeight={400}
                      fontSize={{ xs: "12px" }}
                      color="#606060"
                      mr="4px"
                    >
                      Get all three for just{" "}
                      {CurrencyConverter(insuranceData?.data?.allInsurance)} per
                      month*
                    </Typography>

                    <Typography
                      display="inline"
                      fontWeight={700}
                      fontSize={{ xs: "12px" }}
                      color="#004135"
                      sx={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        router.push(`/${product}/insurance-protection-plan`);
                      }}
                    >
                      Know More
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </PaperComponent>
        <Typography fontWeight={600} fontSize={{ xs: 12, sm: 14 }}>
          {t("nomineeDetails")}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <TextFieldComponent
              required
              allowCapitalization
              label={t("nomineeName")}
              name="nomineeName"
              placeholder={t("nomineePlaceholder")}
              maxLength={60}
              onBlur={formik.handleBlur}
              value={formik.values.nomineeName}
              error={Boolean(
                formik.touched.nomineeName && formik.errors.nomineeName,
              )}
              onChange={(e) => handleFieldChange(e, NAME_REGEX)}
              errorMessage={
                formik.touched.nomineeName && formik.errors.nomineeName
              }
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth>
                {isBelowMd ? (
                  <MobileDatePicker
                    required
                    format="DD/MM/YYYY"
                    maxDate={maxDate}
                    disableFuture
                    label={t("dob")}
                    name="nomineeDob"
                    value={formik?.values?.nomineeDob}
                    onChange={(value) =>
                      formik.setFieldValue("nomineeDob", value).then(() => {
                        formik.setFieldTouched("nomineeDob", true);
                      })
                    }
                    onClose={() => {
                      formik.setFieldTouched("nomineeDob", true);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          Boolean(formik.touched?.nomineeDob) &&
                          Boolean(formik.errors?.nomineeDob),
                        sx: {
                          width: "100%",
                          "& .MuiInputBase-root": {
                            height: 56,
                          },
                          "& .MuiInputBase-input": {
                            fontSize: { xs: "16px", sm: "16px" },
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: { xs: "16px", sm: "14px" },
                          },
                        },
                      },
                      dialog: {
                        sx: {
                          "& .MuiDialog-paper": {
                            width: "calc(100vw - 32px)",
                            maxWidth: 360,
                            borderRadius: "16px",
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <DatePicker
                    required
                    format="DD/MM/YYYY"
                    maxDate={maxDate}
                    disableFuture
                    label={t("dob")}
                    name="nomineeDob"
                    value={formik?.values?.nomineeDob}
                    onChange={(value) =>
                      formik.setFieldValue("nomineeDob", value).then(() => {
                        formik.setFieldTouched("nomineeDob", true);
                      })
                    }
                    onClose={() => {
                      formik.setFieldTouched("nomineeDob", true);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error:
                          Boolean(formik.touched?.nomineeDob) &&
                          Boolean(formik.errors?.nomineeDob),
                      },

                      popper: {
                        disablePortal: true,
                        placement: "bottom-start",
                        modifiers: [
                          {
                            name: "flip",
                            enabled: false,
                          },
                          {
                            name: "preventOverflow",
                            enabled: true,
                          },
                        ],
                        sx: {
                          zIndex: 1600,

                          "& .MuiPaper-root": {
                            width: 235,
                            maxWidth: 235,
                            height: 185,
                            maxHeight: 185,
                            overflow: "hidden",
                            boxShadow: "0px 6px 20px rgba(0,0,0,0.18)",
                          },

                          "& .MuiDateCalendar-root": {
                            width: 235,
                            height: 190,
                            maxHeight: 190,
                          },

                          "& .MuiPickersCalendarHeader-root": {
                            minHeight: 24,
                            maxHeight: 24,
                            px: 2,
                            mt: 0,
                            mb: 0,
                          },

                          "& .MuiPickersCalendarHeader-label": {
                            fontSize: "10px",
                            fontWeight: 600,
                            letterSpacing: 0,
                          },

                          "& .MuiPickersArrowSwitcher-button": {
                            p: 0.25,
                          },

                          "& .MuiDayCalendar-header": {
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 30px)",
                            justifyContent: "center",
                            columnGap: "0px",
                          },
                          "& .MuiDayCalendar-weekContainer": {
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 30px)",
                            justifyContent: "center",
                            columnGap: "0px",
                            margin: 0,
                          },

                          "& .MuiDayCalendar-weekDayLabel": {
                            width: 30,
                            height: 20,
                            fontSize: "10px",
                            mx: 0,
                            justifySelf: "center",
                          },

                          "& .MuiPickersDay-root": {
                            width: 24,
                            height: 24,
                            fontSize: "10px",
                            mx: 0,
                            my: 0,
                            justifySelf: "center",
                          },

                          "& .MuiPickersSlideTransition-root": {
                            minHeight: 140,
                            maxHeight: 140,
                          },

                          "& .MuiDayCalendar-monthContainer": {
                            minHeight: 140,
                            maxHeight: 140,
                          },

                          "& .MuiYearCalendar-root": {
                            width: 235,
                            maxHeight: 160,
                            padding: 0,
                          },

                          "& .MuiPickersYear-yearButton": {
                            fontSize: "10px",
                            width: 52,
                            height: 26,
                            margin: "1px",
                          },

                          "& .MuiPickersYear-yearButton.Mui-selected": {
                            fontSize: "10px",
                          },
                        },
                      },
                    }}
                  />
                )}

                {formik?.touched?.nomineeDob && formik?.errors?.nomineeDob ? (
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {formik?.errors?.nomineeDob}
                  </FormHelperText>
                ) : null}
              </FormControl>
            </LocalizationProvider>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <SelectComponent
              required
              name="nomineeRelation"
              label={t("nomineeRealtion")}
              placeholder={t("relationPlaceholder")}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              defaultValue=""
              singleItemArray={relationData?.data || []}
              value={formik.values.nomineeRelation}
              error={Boolean(
                formik.touched.nomineeRelation && formik.errors.nomineeRelation,
              )}
              errorMessage={
                formik.touched.nomineeRelation && formik.errors.nomineeRelation
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextFieldComponent
              type="number"
              required
              name="nomineeMobileNumber"
              label={t("mobileNumber")}
              placeholder={t("mobilePlaceholder")}
              maxLength={10}
              onBlur={formik.handleBlur}
              value={formik?.values?.nomineeMobileNumber}
              error={Boolean(
                formik?.touched?.nomineeMobileNumber &&
                formik?.errors?.nomineeMobileNumber,
              )}
              multiline={false}
              onChange={(e) => handleFieldChange(e, MOBILE_REGEX)}
              errorMessage={
                formik.touched.nomineeMobileNumber &&
                formik.errors.nomineeMobileNumber
              }
            />
          </Grid>
        </Grid>
        {insuranceData?.data?.isActive &&
          !insuranceData?.data?.insuranceConsent && (
            <Stack>
              <CheckboxComponent
                label={
                  <Typography
                    fontWeight={{ md: 350, xs: 400 }}
                    fontSize={{ xs: "10px", md: "12px" }}
                  >
                    I certify that by selecting Insurance the proposed member is
                    in good health, free from mental or physical impairment & do
                    not suffer from any chronic or pre-existing disease.
                  </Typography>
                }
                checked={insuranceDeclaration}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  dispatch(setInsuranceDeclaration(isChecked));
                }}
              />
            </Stack>
          )}

        <ButtonComponent
          type="submit"
          onClick={formik.handleSubmit}
          disabled={
            !(formik?.isValid && formik?.dirty) ||
            (insuranceData?.data?.isActive &&
              !insuranceData?.data?.insuranceConsent &&
              !insuranceDeclaration)
          }
        >
          {t("proceed")}
        </ButtonComponent>
      </Stack>
    </ModalComponent>
  );
};

export default NomineeFields;
